import { sendProofOfPlay } from "../api/deviceClient";
import type { PopRecord } from "../api/types";
import { getBridge } from "../bridge";
import { usePlayerStore } from "../state/playerStore";

// Offline-tolerant proof-of-play buffer. Records are appended locally and
// persisted (so they survive a reload/crash), then flushed to the backend in
// batches. On a failed flush the batch is kept and retried.

const POP_BUFFER_KEY = "gocityscreens.pop.buffer";
const MAX_BUFFER = 5_000; // backstop against unbounded growth when long-offline

// Each record remembers the screen it played on (never sent). A device that is
// unpaired and re-paired keeps the records for the SAME screen — they are real,
// unsettled delivery — and drops the ones for any other screen.
type BufferedPop = PopRecord & { screen_id?: string };

let buffer: BufferedPop[] = [];
let loaded = false;
let flushing = false;

async function persist(): Promise<void> {
  await getBridge().secureStore.set(POP_BUFFER_KEY, JSON.stringify(buffer));
}

async function ensureLoaded(): Promise<void> {
  if (loaded) return;
  const raw = await getBridge().secureStore.get(POP_BUFFER_KEY);
  if (raw) {
    try {
      buffer = JSON.parse(raw) as BufferedPop[];
    } catch {
      buffer = [];
    }
  }
  loaded = true;
}

export async function addPop(record: PopRecord): Promise<void> {
  await ensureLoaded();
  const screenId = usePlayerStore.getState().screen?.screen_id;
  buffer.push(screenId ? { ...record, screen_id: screenId } : { ...record });
  if (buffer.length > MAX_BUFFER) buffer = buffer.slice(-MAX_BUFFER);
  await persist();
}

export async function flushPop(): Promise<void> {
  await ensureLoaded();
  if (flushing || buffer.length === 0) return;
  flushing = true;
  const batch = buffer.slice();
  try {
    const result = await sendProofOfPlay({ records: batch.map(({ screen_id: _tag, ...r }) => r) });
    // The server's verdict is final either way (its audit row keeps a sample of
    // what it refused), but deleting evidence in SILENCE would hide a whole failure
    // class: a drifted clock stamping records outside the trust-gate window
    // looks identical to everything landing.
    const rejected = result?.rejected ?? 0;
    if (rejected > 0) {
      console.warn(
        `[popBuffer] server rejected ${rejected}/${batch.length} proof-of-play ` +
          `record(s) (accepted ${result.inserted}) — unscheduled or outside the slot ` +
          "window; if the clock-jump warning also fired, the local clock drifted",
      );
    }
    // Remove exactly what we sent (new records may have arrived meanwhile).
    buffer = buffer.slice(batch.length);
    await persist();
  } catch (err) {
    console.warn(`[popBuffer] flush failed, will retry (${buffer.length} buffered)`, err);
  } finally {
    flushing = false;
  }
}

export function bufferedCount(): number {
  return buffer.length;
}

/**
 * After (re-)pairing: keep only the records played on `screenId`. They are still
 * owed to that screen's bookings; records for another screen (or untagged, from an
 * older build) would only be rejected by the backend's trust gate.
 */
export async function retainPopForScreen(screenId: string): Promise<void> {
  await ensureLoaded();
  // A flush still in flight slices the buffer by count when it lands; filtering
  // under it would drop the wrong records. Untouched records are merely rejected.
  if (flushing) return;
  const kept = buffer.filter((r) => r.screen_id === screenId);
  if (kept.length === buffer.length) return;
  buffer = kept;
  await persist();
}

// test-only reset
export function __reset(): void {
  buffer = [];
  loaded = false;
  flushing = false;
}
